import { nhost } from 'utility'
import { compressImage } from './compress-image'
import { MAX_QUESTION_IMAGE_SIZE_KB } from 'constants/imageUpload'
import {
  ALLOWED_AUDIO_MIME_TYPES,
  ALLOWED_IMAGE_MIME_TYPES,
  ALLOWED_VIDEO_MIME_TYPES,
  COMPRESSIBLE_IMAGE_MIME_TYPES,
  MAX_QUESTION_AUDIO_SIZE_KB,
  MAX_QUESTION_GIF_SIZE_KB,
  MAX_QUESTION_VIDEO_SIZE_KB,
  MIME_TYPE_EXTENSIONS,
} from 'constants/mediaUpload'

export type MediaKind = 'image' | 'gif' | 'audio' | 'video'

/** Narrow a MIME type to the class that decides its size cap and handling. */
export const classifyMedia = (mimeType?: string): MediaKind | null => {
  const type = (mimeType || '').toLowerCase()
  if (type === 'image/gif') return 'gif'
  if (ALLOWED_IMAGE_MIME_TYPES.includes(type)) return 'image'
  if (ALLOWED_AUDIO_MIME_TYPES.includes(type)) return 'audio'
  if (ALLOWED_VIDEO_MIME_TYPES.includes(type)) return 'video'
  return null
}

export const maxSizeKbFor = (kind: MediaKind): number => {
  switch (kind) {
    case 'gif':
      return MAX_QUESTION_GIF_SIZE_KB
    case 'audio':
      return MAX_QUESTION_AUDIO_SIZE_KB
    case 'video':
      return MAX_QUESTION_VIDEO_SIZE_KB
    default:
      return MAX_QUESTION_IMAGE_SIZE_KB
  }
}

const formatMb = (kb: number) => `${Math.round((kb / 1024) * 10) / 10} MB`

export const unsupportedMediaMessage = () =>
  'Unsupported file. Allowed: PNG, JPEG, GIF, audio (MP3, M4A, OGG, WAV, WebM) and video (MP4, WebM, OGV).'

export const oversizeMediaMessage = (kind: MediaKind, actualKb: number) =>
  `${kind === 'gif' ? 'GIFs' : kind === 'audio' ? 'Audio files' : kind === 'video' ? 'Videos' : 'Images'} larger than ${formatMb(
    maxSizeKbFor(kind)
  )} cannot be uploaded. Your file is ${formatMb(actualKb)}.`

/** Build a collision-resistant filename; blobs from clipboard paste arrive unnamed. */
export const buildMediaFileName = (mimeType: string, originalName?: string): string => {
  const extension = MIME_TYPE_EXTENSIONS[(mimeType || '').toLowerCase()] || 'bin'
  if (originalName && originalName.includes('.')) return originalName
  const timestamp = new Date().getTime()
  const randomString = Math.random().toString(36).substring(2, 8)
  return `${timestamp}_${randomString}.${extension}`
}

export type MediaUploadResult =
  | { ok: true; url: string; fileId: string; kind: MediaKind; name: string }
  | { ok: false; message: string }

/**
 * Single entry point for putting question media into storage.
 *
 * Compression only applies to PNG/JPEG. A GIF pushed through compressImage would come
 * back as a single frame, so oversized GIFs are rejected rather than silently flattened.
 * Audio and video are never re-encoded in the browser.
 */
export const uploadQuestionMedia = async (file: File): Promise<MediaUploadResult> => {
  const kind = classifyMedia(file.type)
  if (!kind) {
    return { ok: false, message: unsupportedMediaMessage() }
  }

  let outgoing = file
  const limitKb = maxSizeKbFor(kind)

  if (COMPRESSIBLE_IMAGE_MIME_TYPES.includes(file.type.toLowerCase()) && outgoing.size / 1024 > limitKb) {
    outgoing = await compressImage(outgoing)
  }

  const finalKb = outgoing.size / 1024
  if (finalKb > limitKb) {
    return { ok: false, message: oversizeMediaMessage(kind, finalKb) }
  }

  const { fileMetadata, error } = await nhost.storage.upload({ file: outgoing })
  const fileId = fileMetadata?.id
  if (error || !fileId) {
    return { ok: false, message: 'Upload failed. Please try again.' }
  }

  const url = nhost.storage.getPublicUrl({ fileId })
  return { ok: true, url, fileId, kind, name: fileMetadata?.name || outgoing.name }
}

/**
 * Printed papers and PDF exports cannot play media, so audio/video become a labelled
 * box instead. Commas are stripped because the paper export serialises through a CSV
 * where "," is the field separator (it escapes real commas to !@!).
 */
export const buildMediaPlaceholder = (kind: 'audio' | 'video', label?: string): string => {
  const safeLabel = (label || '').replace(/,/g, ' ').trim()
  const text = `${kind === 'audio' ? 'Audio' : 'Video'}${safeLabel ? `: ${safeLabel}` : ''}`
  return `<span style="display:inline-block;border:1px dashed #888888;padding:4px 10px;font-style:italic;">[${text}]</span>`
}

/** Placeholder markup for every <audio>/<video> in a parsed question body, or '' if there is none. */
export const mediaPlaceholdersFrom = (body?: HTMLElement | null): string => {
  if (!body) return ''
  return Array.from(body.querySelectorAll('audio, video'))
    .map(node => {
      const kind = node.tagName.toLowerCase() === 'audio' ? 'audio' : 'video'
      // SunEditor keeps the original filename here; TinyMCE puts it in title.
      const label = node.getAttribute('data-file-name') || node.getAttribute('title') || ''
      return buildMediaPlaceholder(kind, label)
    })
    .join(' ')
}

/**
 * Strip <audio>/<video> out of raw question HTML. The export keeps the original HTML
 * string (rather than re-serialising the DOM) to avoid disturbing its escaping, so the
 * tags have to come out textually before placeholders are appended.
 */
export const stripMediaTags = (html?: string): string =>
  (html || '').replace(/<(audio|video)\b[^>]*>[\s\S]*?<\/\1>/gi, '').replace(/<(audio|video)\b[^>]*\/?>/gi, '')

/** Turn a data: URL (clipboard paste / editor base64) into a File we can validate and upload. */
export const dataUrlToFile = (dataUrl: string, fallbackName?: string): File | null => {
  const match = dataUrl.match(/^data:([^;]+);base64,/)
  if (!match) return null
  const mimeType = match[1]
  const base64Data = dataUrl.slice(match[0].length)
  try {
    const byteCharacters = atob(base64Data)
    const byteNumbers = new Array(byteCharacters.length)
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i)
    }
    const byteArray = new Uint8Array(byteNumbers)
    return new File([byteArray], buildMediaFileName(mimeType, fallbackName), { type: mimeType })
  } catch (err) {
    console.log(err, ':error converting data url to file')
    return null
  }
}
